/**
 * Reviews slider — COREEX
 * One review per slide, draggable, with dash-style dots and circular
 * arrows.
 *
 * The controls are rendered inside every slide's content column rather
 * than once outside the slider, so they travel with the review they
 * belong to instead of hovering over the images mid-transition. That
 * rules out Swiper's own navigation/pagination modules, which expect a
 * single element each — so the handlers are delegated from the section
 * root and every copy's state is synced on each change.
 */
(function () {
  var ROOT_SELECTOR = '[data-coreex-reviews]';

  document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
    markGappedMedia(root);
    initSlider(root);
  });

  /**
   * The before/after pair reads as a single image when the gap is 0, so
   * the CSS squares off the seam in that case. Any gap above 0 makes
   * them two separate cards, and all four corners round.
   */
  function markGappedMedia(root) {
    var gap = parseFloat(
      getComputedStyle(root).getPropertyValue('--rv-img-gap')
    ) || 0;

    if (gap <= 0) return;

    root.querySelectorAll('.review__media').forEach(function (media) {
      media.classList.add('review__media--gapped');
    });
  }

  function initSlider(root) {
    var container = root.querySelector('.coreex-reviews__swiper');
    if (!container || typeof Swiper === 'undefined') return;

    var slideCount = container.querySelectorAll('.swiper-slide').length;
    var speed = parseInt(root.dataset.speed, 10) || 600;
    // Looping a single slide leaves Swiper with nothing to loop between.
    var loop = root.dataset.loop === 'true' && slideCount > 1;

    var swiper = new Swiper(container, {
      slidesPerView: 1,
      speed: speed,
      loop: loop,
      grabCursor: true,
      watchOverflow: true,
      // Height tracks the active slide instead of the tallest one — but ONLY
      // below 1000px. There the slide is one stacked review whose length varies
      // a lot, so a fixed height left short reviews with a big empty gap. At
      // >=1000px the review is a fixed-height side-by-side card and autoHeight
      // would let the media image drive the height too tall, so it is turned
      // back off there to restore the original desktop behaviour.
      autoHeight: true,
      breakpoints: {
        1000: { autoHeight: false }
      }
    });

    bindControls(root, swiper);
    syncControls(root, swiper);

    swiper.on('slideChange', function () {
      syncControls(root, swiper);
    });
  }

  /**
   * Delegated from the root so the handlers survive any DOM shuffling
   * Swiper does in loop mode, and so a section with twenty reviews
   * doesn't end up with sixty individual listeners.
   *
   * Capture phase, deliberately: Swiper's preventClicksPropagation
   * calls stopPropagation() on the wrapper after any drag, which would
   * keep a bubbling listener on the root from ever seeing the click.
   */
  function bindControls(root, swiper) {
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-reviews-prev]')) {
        swiper.slidePrev();
        return;
      }

      if (target.closest('[data-reviews-next]')) {
        swiper.slideNext();
        return;
      }

      var dot = target.closest('[data-reviews-dot]');
      if (!dot) return;

      var index = parseInt(dot.dataset.index, 10);
      if (isNaN(index)) return;

      if (swiper.params.loop) {
        swiper.slideToLoop(index);
      } else {
        swiper.slideTo(index);
      }
    }, true);
  }

  /** Mirror the current position onto every copy of the controls. */
  function syncControls(root, swiper) {
    var index = swiper.realIndex;

    root.querySelectorAll('[data-reviews-dot]').forEach(function (dot) {
      var isActive = parseInt(dot.dataset.index, 10) === index;
      dot.classList.toggle('coreex-reviews__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // With looping there is no first or last slide, so the arrows never
    // reach a dead end and shouldn't be dimmed.
    var atStart = !swiper.params.loop && swiper.isBeginning;
    var atEnd = !swiper.params.loop && swiper.isEnd;

    root.querySelectorAll('[data-reviews-prev]').forEach(function (button) {
      button.classList.toggle('is-disabled', atStart);
      button.disabled = atStart;
    });

    root.querySelectorAll('[data-reviews-next]').forEach(function (button) {
      button.classList.toggle('is-disabled', atEnd);
      button.disabled = atEnd;
    });
  }
})();
